import ProfileModal from './ProfileModal';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useTheme } from '../context/ThemeContext';
// import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import { User } from '../types';
import SettingsModal from './SettingsModal';
import { apiService, UserSettings } from '../services/api';

// Import icons
import { FiUser, FiSettings, FiMoon, FiSun, FiHelpCircle, FiLogOut } from 'react-icons/fi';


interface MenuButtonProps {
  theme: 'light' | 'dark';
}

const MenuButton = styled.div<MenuButtonProps>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  user-select: none;
  
  &:hover {
    background-color: ${({ theme }) => theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const UserAvatar = styled.div<{ theme: 'light' | 'dark' }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #274797;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
`;

const UserName = styled.span<{ theme: 'light' | 'dark' }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme === 'dark' ? '#ffffff' : '#2d333a'};
  white-space: nowrap;
`;

const UserEmail = styled.span<{ theme: 'light' | 'dark' }>`
  font-size: 0.75rem;
  color: ${({ theme }) => theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'};
`;

// Use a transient prop ($isOpen) so React does not forward it to the DOM
const DropdownMenu = styled.div<{ theme: 'light' | 'dark', $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  width: 240px;
  background: ${({ theme }) => theme === 'dark' ? '#2d3748' : '#ffffff'};
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  z-index: 50;
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(10px)')};
  transition: all 0.2s ease-in-out;
  border: 1px solid ${({ theme }) => theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
`;

const MenuItem = styled.div<{ theme: 'light' | 'dark' }>`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: ${({ theme }) => theme === 'dark' ? '#e2e8f0' : '#2d3748'};
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-size: 0.875rem;
  gap: 0.75rem;

  &:hover {
    background-color: rgba(66,153,225,0.11);
  }

  svg {
    font-size: 1.1rem;
    color: ${({ theme }) => theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'};
  }
`;

const Divider = styled.div<{ theme: 'light' | 'dark' }>`
  height: 1px;
  background-color: ${({ theme }) => theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  margin: 0.25rem 0;
`;

interface UserMenuProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  onLogout: () => void;
  user: Pick<User, 'firstName' | 'lastName' | 'email'> | null;
}

const UserMenu: React.FC<UserMenuProps> = ({ theme, toggleTheme, onLogout, user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [userSettings, setUserSettings] = useState<UserSettings | undefined>(undefined);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch user settings from backend on mount
  useEffect(() => {
    let mounted = true;
    setSettingsLoading(true);
    setSettingsError(null);
    apiService.getUserSettings()
      .then(res => {
        if (mounted && res.success && res.data && res.data.settings) {
          setUserSettings(res.data.settings);
        }
      })
      .catch(err => {
        setSettingsError('Failed to load user settings');
      })
      .finally(() => setSettingsLoading(false));
    return () => { mounted = false; };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleProfileClick = () => {
    setIsProfileOpen(true);
    setIsOpen(false);
  };

  const handleSettingsClick = () => {
    setIsSettingsOpen(true);
    setIsOpen(false);
  };

  const handleSettingsClose = () => {
    setIsSettingsOpen(false);
  };

  const handleSettingsSave = async (values: UserSettings) => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const res = await apiService.updateUserSettings(values);
      if (res.success && res.data && res.data.settings) {
        setUserSettings(res.data.settings);
      }
    } catch (e) {
      setSettingsError('Failed to save user settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleThemeToggle = () => {
    toggleTheme();
  };

  const handleHelpClick = () => {
    navigate('/help');
    setIsOpen(false);
  };

  const handleLogoutClick = () => {
    onLogout();
    setIsOpen(false);
  };

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      <MenuButton theme={theme} onClick={handleToggleMenu}>
        <UserAvatar theme={theme}>
          {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
        </UserAvatar>
        <UserInfo>
          <UserName theme={theme}>
            {user?.firstName} {user?.lastName}
          </UserName>
        </UserInfo>
      </MenuButton>

  <DropdownMenu theme={theme} $isOpen={isOpen}>
        <div style={{ padding: '0.5rem 0' }}>
          <MenuItem theme={theme} onClick={handleProfileClick}>
            <FiUser />
            <span>Profile</span>
          </MenuItem>
          
          <MenuItem theme={theme} onClick={handleSettingsClick}>
            <FiSettings />
            <span>Settings</span>
          </MenuItem>
          
          <MenuItem theme={theme} onClick={handleThemeToggle}>
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </MenuItem>
          
          <Divider theme={theme} />
          
          <MenuItem theme={theme} onClick={handleHelpClick}>
            <FiHelpCircle />
            <span>Help & Support</span>
          </MenuItem>
          
          <Divider theme={theme} />
          
          <MenuItem theme={theme} onClick={handleLogoutClick}>
            <FiLogOut />
            <span>Logout</span>
          </MenuItem>
        </div>
      </DropdownMenu>
      
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={handleSettingsClose}
        theme={theme}
        initialValues={userSettings}
        onSave={handleSettingsSave}
        loading={settingsLoading}
        error={settingsError}
      />
      <ProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        theme={theme}
        user={user}
      />
    </div>
  );
};

export default UserMenu;
